import { config } from "@/config";
import {
  Canvas,
  CanvasPublishResponse,
  CreateCanvasInWorkspaceRequest,
  FlowSession,
  UpdateCanvasRequest,
  Workspace,
} from "@cohesive/api-client";
import {
  type QueryClient,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { orgAPI } from "./api";
import { invalidateArchivedQueries } from "./archived";
import { usePrivateWorkspace, useWorkspaces } from "./workspaces";

/**
 * Default name for a freshly-created canvas. This MUST stay in sync with the
 * backend auto-name guard (`canvas_name_generate.go`), which only replaces a
 * canvas name when it still equals this exact literal. If the two drift, new
 * canvases never get an AI-generated title.
 */
export const DEFAULT_CANVAS_NAME = "Untitled";

/**
 * Optimistically updates `hero_generating` on cached canvas detail and list entries.
 */
export function setCanvasHeroGenerating(
  queryClient: QueryClient,
  canvasId: string,
  hero_generating: boolean,
) {
  queryClient.setQueryData<Canvas>(["canvas", canvasId], (old) => {
    if (!old) return old;
    return {
      ...old,
      metadata: { ...old.metadata, hero_generating },
    };
  });
  queryClient.setQueryData<Canvas[]>(["canvases"], (old) => {
    if (!old) return old;
    return old.map((c) =>
      String(c.id) === canvasId
        ? { ...c, metadata: { ...c.metadata, hero_generating } }
        : c,
    );
  });
}

/**
 * Patch publish fields on cached canvas detail + every canvases list query.
 */
function patchCanvasPublishFields(
  queryClient: QueryClient,
  canvasId: string,
  patch: Pick<Canvas, "published_status" | "published_at">,
) {
  queryClient.setQueryData<Canvas>(["canvas", canvasId], (old) => {
    if (!old) return old;
    return { ...old, ...patch };
  });
  for (const [key, list] of queryClient.getQueriesData<Canvas[]>({
    queryKey: ["canvases"],
  })) {
    if (!Array.isArray(list)) continue;
    queryClient.setQueryData<Canvas[]>(key, (old) => {
      if (!old) return old;
      return old.map((c) =>
        String(c.id) === canvasId ? { ...c, ...patch } : c,
      );
    });
  }
}

const PUBLISH_POLL_INTERVAL_MS = 750;
const PUBLISH_POLL_TIMEOUT_MS = 90_000;

/** In-flight publish poll AbortControllers, keyed by canvas id. */
const publishPollControllers = new Map<string, AbortController>();

type CanvasPublishPollBaseline = {
  published_status: string;
  published_at?: string;
  // updated_at distinguishes a no-op republish restore (same published_at,
  // bumped updated_at) from a lagging GET of the pre-job row.
  updated_at?: string;
};

function abortPublishPoll(canvasId: string) {
  const existing = publishPollControllers.get(canvasId);
  if (!existing) return;
  existing.abort();
  publishPollControllers.delete(canvasId);
}

/**
 * True when a GET still mirrors the pre-publish row. The publish POST returns
 * 202 before the workflow writes `publishing`, so early polls can still see
 * `""`, prior `published`, or prior `failed`.
 *
 * No-op republish restores `published` with the same `published_at`; without
 * `updated_at` that terminal row looks identical to the baseline and the poll
 * would ignore a successful job forever when the WS event is missed.
 */
export function isStaleCanvasPublishPollSnapshot(
  canvas: Pick<Canvas, "published_status" | "published_at" | "updated_at">,
  baseline: CanvasPublishPollBaseline,
): boolean {
  const status = canvas.published_status ?? "";
  return (
    status === baseline.published_status &&
    (canvas.published_at ?? undefined) ===
      (baseline.published_at ?? undefined) &&
    (canvas.updated_at ?? undefined) === (baseline.updated_at ?? undefined)
  );
}

/**
 * Poll GET canvas until `published_status` leaves `publishing` (or timeout).
 * Safety net when the WS `canvas.updated` event is missed or delayed.
 *
 * Ignores GETs that still match `baseline` (the row before this publish) so a
 * lagging response cannot overwrite optimistic `publishing` or end the poll
 * before the job is visible. Accepts a fast `published`/`failed` that diverges
 * from baseline even if we never observed the intermediate `publishing` tick.
 * Aborts when a newer publish poll starts or the canvas is unpublished.
 */
async function pollCanvasPublishTerminal(
  queryClient: QueryClient,
  canvasId: string,
  signal: AbortSignal,
  baseline: CanvasPublishPollBaseline,
): Promise<void> {
  const deadline = Date.now() + PUBLISH_POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    if (signal.aborted) return;
    try {
      const canvas = await orgAPI.canvas.getById(canvasId);
      if (signal.aborted) return;

      if (!isStaleCanvasPublishPollSnapshot(canvas, baseline)) {
        const status = canvas.published_status ?? "";
        patchCanvasPublishFields(queryClient, canvasId, {
          published_status: status,
          published_at: canvas.published_at,
        });
        if (status !== "publishing") {
          return;
        }
      }
      // else: still the pre-job snapshot — keep optimistic publishing
    } catch {
      // Keep polling through transient errors.
    }
    if (signal.aborted) return;
    await new Promise((resolve) =>
      setTimeout(resolve, PUBLISH_POLL_INTERVAL_MS),
    );
  }

  if (signal.aborted) return;

  // Avoid leaving the Share UI stuck on optimistic "publishing" forever when
  // WS + poll both miss a terminal status — refetch from the server.
  await queryClient.invalidateQueries({ queryKey: ["canvas", canvasId] });
  await queryClient.invalidateQueries({ queryKey: ["canvases"] });
}

function startCanvasPublishPoll(
  queryClient: QueryClient,
  canvasId: string,
  baseline: CanvasPublishPollBaseline,
) {
  abortPublishPoll(canvasId);
  const controller = new AbortController();
  publishPollControllers.set(canvasId, controller);
  void pollCanvasPublishTerminal(
    queryClient,
    canvasId,
    controller.signal,
    baseline,
  ).finally(() => {
    if (publishPollControllers.get(canvasId) === controller) {
      publishPollControllers.delete(canvasId);
    }
  });
}

function isIPv4Hostname(hostname: string): boolean {
  const parts = hostname.split(".");
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    if (!/^\d{1,3}$/.test(part)) return false;
    const n = Number(part);
    return n >= 0 && n <= 255;
  });
}

/**
 * For server-side capture, prefer an explicit capture host when configured
 * (`VITE_CAPTURE_BASE_URL`). PR previews must use a stable host (app.dev) —
 * the screenshot lambda loads `{origin}/canvas-capture.html`, and capturing
 * against ephemeral `app-pr-*` origins is unreliable.
 *
 * When unset: use the current origin on a real hostname; on localhost / raw IP,
 * fall back to `VITE_APP_BASE_URL` then origin.
 */
function resolveCanvasScreenshotAppBaseUrl(): string {
  const configuredCapture = (config.hosts.capture || "").replace(/\/$/, "");
  if (configuredCapture) {
    return configuredCapture;
  }

  const origin = window.location.origin;
  const hostname = window.location.hostname;
  const isLocalhostOrIp =
    hostname.toLowerCase() === "localhost" ||
    isIPv4Hostname(hostname) ||
    hostname.includes(":");
  if (!isLocalhostOrIp) {
    return origin;
  }
  return (config.hosts.app || origin).replace(/\/$/, "");
}

/**
 * Public share URL for a published canvas. Host comes from `config.hosts.share`
 * (`VITE_SHARE_BASE_URL`; empty falls back to `window.location.origin`); path is
 * `/view/canvas/{id}`.
 */
export function buildCanvasPublicUrl(canvasId: string): string {
  const base = (config.hosts.share || window.location.origin).replace(
    /\/$/,
    "",
  );
  return `${base}/view/canvas/${encodeURIComponent(canvasId)}`;
}

/**
 * Enqueues server-side canvas hero capture (`POST .../capture-screenshot`) and toggles
 * `metadata.hero_generating` in the React Query cache. Swallows HTTP 501 (feature off).
 */
export function useCreateCanvasScreenshot(
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (canvasId: string) => {
      if (!canvasId) return;
      try {
        await orgAPI.canvas.captureScreenshot(canvasId, {
          appBaseUrl: resolveCanvasScreenshotAppBaseUrl(),
        });
        setCanvasHeroGenerating(queryClient, canvasId, true);
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 501) {
          return;
        }
        setCanvasHeroGenerating(queryClient, canvasId, false);
      }
    },
  });
}

/**
 * Kick off async public-canvas publish. Optimistically sets `published_status`
 * to `publishing`; terminal status arrives via `canvas.updated` (with GET poll
 * fallback).
 */
type CanvasPublishMutationContext = {
  previousDetail?: Canvas;
};

export function usePublishCanvas(
  options?: UseMutationOptions<
    CanvasPublishResponse,
    AxiosError,
    string,
    CanvasPublishMutationContext
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (canvasId: string) =>
      orgAPI.canvas.publish(canvasId, {
        appBaseUrl: resolveCanvasScreenshotAppBaseUrl(),
      }),
    ...options,
    onMutate: async (canvasId, ...rest) => {
      abortPublishPoll(canvasId);
      await queryClient.cancelQueries({ queryKey: ["canvas", canvasId] });
      const previousDetail = queryClient.getQueryData<Canvas>([
        "canvas",
        canvasId,
      ]);
      patchCanvasPublishFields(queryClient, canvasId, {
        published_status: "publishing",
      });
      const fromOptions = await options?.onMutate?.(canvasId, ...rest);
      return { previousDetail, ...fromOptions };
    },
    onError: (error, canvasId, context, ...rest) => {
      if (context?.previousDetail) {
        // Restore detail + every canvases list entry (onMutate patched both).
        patchCanvasPublishFields(queryClient, canvasId, {
          published_status: context.previousDetail.published_status ?? "",
          published_at: context.previousDetail.published_at,
        });
        queryClient.setQueryData(["canvas", canvasId], context.previousDetail);
      }
      options?.onError?.(error, canvasId, context, ...rest);
    },
    onSuccess: async (data, canvasId, context, ...rest) => {
      startCanvasPublishPoll(queryClient, canvasId, {
        published_status: context?.previousDetail?.published_status ?? "",
        published_at: context?.previousDetail?.published_at,
        updated_at: context?.previousDetail?.updated_at,
      });
      await options?.onSuccess?.(data, canvasId, context, ...rest);
    },
  });
}

/**
 * Revoke the public canvas snapshot. Optimistically clears publish fields.
 */
export function useUnpublishCanvas(
  options?: UseMutationOptions<
    void,
    AxiosError,
    string,
    CanvasPublishMutationContext
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (canvasId: string) => orgAPI.canvas.unpublish(canvasId),
    ...options,
    onMutate: async (canvasId, ...rest) => {
      abortPublishPoll(canvasId);
      await queryClient.cancelQueries({ queryKey: ["canvas", canvasId] });
      const previousDetail = queryClient.getQueryData<Canvas>([
        "canvas",
        canvasId,
      ]);
      patchCanvasPublishFields(queryClient, canvasId, {
        published_status: "",
        published_at: undefined,
      });
      const fromOptions = await options?.onMutate?.(canvasId, ...rest);
      return { previousDetail, ...fromOptions };
    },
    onError: (error, canvasId, context, ...rest) => {
      if (context?.previousDetail) {
        patchCanvasPublishFields(queryClient, canvasId, {
          published_status: context.previousDetail.published_status ?? "",
          published_at: context.previousDetail.published_at,
        });
        queryClient.setQueryData(["canvas", canvasId], context.previousDetail);
      }
      options?.onError?.(error, canvasId, context, ...rest);
    },
    onSuccess: (...args) => {
      const [, canvasId] = args;
      abortPublishPoll(canvasId);
      // Ensure detail cache reflects cleared publish state even if onMutate
      // raced a WS event that re-set publishing.
      patchCanvasPublishFields(queryClient, canvasId, {
        published_status: "",
        published_at: undefined,
      });
      options?.onSuccess?.(...args);
    },
  });
}

export function useCanvases(
  options?: Partial<UseQueryOptions<Canvas[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["canvases", "all"],
    queryFn: async () => {
      const workspaces = await orgAPI.workspaces
        .list()
        .then((workspaces) =>
          workspaces.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
          ),
        );

      const fetches: Promise<Canvas[]>[] = [
        // Directly shared canvases
        orgAPI.canvas.listSharedWithMe().catch(() => []),
        // All workspace canvases in parallel
        ...(workspaces ?? []).map((ws) =>
          orgAPI.workspaces.listCanvases(ws.id).catch(() => []),
        ),
      ];

      const results = await Promise.all(fetches);
      const all = results.flat();

      // Deduplicate by canvas ID (a canvas could appear in both a workspace
      // and shared-with-me if pruning hasn't run yet).
      const seen = new Set<string>();
      return all
        .filter((c) => {
          if (seen.has(c.id)) return false;
          seen.add(c.id);
          return true;
        })
        .sort(
          (a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        );
    },
    enabled: options?.enabled ?? true,
    ...options,
  });
}

export function useSharedWithMeCanvases(
  options?: Partial<UseQueryOptions<Canvas[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["canvas", "sharedWithMe"],
    queryFn: () => orgAPI.canvas.listSharedWithMe(),
    ...options,
  });
}

/**
 * Recent canvases the user can access, ordered most-recently-modified first.
 * Backed by `GET .../canvases?limit=`. The `["canvases", ...]` key prefix means
 * canvas mutations (create/update/delete/duplicate) already invalidate this.
 */
export function useRecentCanvases(
  limit = 8,
  options?: Partial<UseQueryOptions<Canvas[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["canvases", "recent", limit],
    queryFn: () => orgAPI.canvas.list({ limit }),
    ...options,
  });
}

export interface WorkspaceSidebarData {
  workspaces: Workspace[];
  sharedWithMe: Canvas[];
}

export function useWorkspaceSidebarData() {
  const {
    data: workspaces,
    isPending: workspacesPending,
    isError: workspacesError,
  } = useWorkspaces();

  const sharedQuery = useSharedWithMeCanvases();

  const isPending = workspacesPending || sharedQuery.isPending;
  const isError = workspacesError || sharedQuery.isError;

  const data: WorkspaceSidebarData | undefined =
    workspaces && sharedQuery.data
      ? {
          workspaces: workspaces ?? [],
          sharedWithMe: sharedQuery.data ?? [],
        }
      : undefined;

  return { data, isPending, isError };
}

export function useCanvasById(
  canvasId: string | undefined,
  options?: Partial<UseQueryOptions<Canvas, AxiosError>>,
) {
  return useQuery({
    queryKey: ["canvas", canvasId],
    queryFn: () => {
      if (!canvasId) {
        throw new Error("Canvas ID is required");
      }
      return orgAPI.canvas.getById(canvasId);
    },
    enabled: !!canvasId && (options?.enabled ?? true),
    ...options,
  });
}

export type CreateCanvasVariables = Omit<
  CreateCanvasInWorkspaceRequest,
  "name"
> & {
  /** Canvas name. Defaults to {@link DEFAULT_CANVAS_NAME} when omitted/blank. */
  name?: string;
  /** Workspace to create in. Defaults to the current user's private workspace. */
  workspaceId?: string;
};

export function useCreateCanvas(
  options?: UseMutationOptions<Canvas, AxiosError, CreateCanvasVariables>,
) {
  const queryClient = useQueryClient();
  const privateWorkspace = usePrivateWorkspace();
  const { mutateAsync: captureCanvasScreenshot } = useCreateCanvasScreenshot();

  return useMutation({
    mutationFn: ({ workspaceId, name, ...data }: CreateCanvasVariables) => {
      const targetId = workspaceId ?? privateWorkspace?.id;
      if (!targetId) {
        throw new Error("No workspace available to create canvas in");
      }
      return orgAPI.workspaces.createCanvas(targetId, {
        ...data,
        name: name?.trim() || DEFAULT_CANVAS_NAME,
      });
    },
    ...options,
    onSuccess: (...args) => {
      const [data] = args;
      // Invalidate every cache that includes canvas data so creates reflect
      // everywhere (workspace lists, recent_canvases, shared-with-me, etc.).
      queryClient.invalidateQueries({ queryKey: ["canvases"] });
      queryClient.invalidateQueries({ queryKey: ["canvas"] });
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["workspace"] });
      void captureCanvasScreenshot(data.id);
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateCanvas(
  options?: UseMutationOptions<
    Canvas,
    AxiosError,
    { id: string; data: UpdateCanvasRequest }
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCanvasRequest }) =>
      orgAPI.canvas.update(id, data),
    ...options,
    onSuccess: (...args) => {
      const [, { id }] = args;
      // Invalidate every cache that includes canvas data so renames, moves,
      // and metadata edits reflect immediately everywhere.
      queryClient.invalidateQueries({ queryKey: ["canvases"] });
      queryClient.invalidateQueries({ queryKey: ["canvas"] });
      queryClient.invalidateQueries({ queryKey: ["canvas", id] });
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["workspace"] });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteCanvas(
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => orgAPI.canvas.delete(id),
    ...options,
    onSuccess: (...args) => {
      // Deleting a canvas cascades to its chats and files on the backend, so
      // invalidate every cache that can contain trashed resources.
      invalidateArchivedQueries(queryClient);
      options?.onSuccess?.(...args);
    },
  });
}

export function useDuplicateCanvas(
  options?: UseMutationOptions<Canvas, AxiosError, string>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (canvasId: string) => orgAPI.canvas.duplicate(canvasId),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ["canvases"] });
      queryClient.invalidateQueries({ queryKey: ["canvas"] });
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["workspace"] });
      options?.onSuccess?.(...args);
    },
  });
}

export function useCanvasFlowSessions(
  canvasId: string | undefined,
  options?: Partial<UseQueryOptions<FlowSession[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["canvas-flow-sessions", canvasId],
    queryFn: () => {
      if (!canvasId) {
        throw new Error("Canvas ID is required");
      }
      return orgAPI.canvas.getFlowSessions(canvasId);
    },
    enabled: !!canvasId && (options?.enabled ?? true),
    ...options,
  });
}
